/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiContract, ApplicationContract, FailureContract, FailureResponse } from '../models/framework.model';
import { LoggerService } from './logger.service';

/**
 * Structured context captured for every 4xx/5xx HTTP error (AOS-1522).
 * Contains only safe, non-PII fields for logging and debugging.
 */
export interface HttpErrorContext {
  /** HTTP status code (e.g. 403, 500). */
  readonly statusCode: number;
  /** Human-readable error classification label. */
  readonly errorClass: '4xx - Client-Side Error' | '5xx - Server-Side Error' | 'Network Error';
  /** The URL of the failing request, without query parameters to avoid leaking data. */
  readonly endpoint: string;
  /** HTTP method of the failing request (GET, POST, etc.). */
  readonly method: string;
  /** ISO-8601 timestamp of when the error was observed on the client. */
  readonly timestamp: string;
  /** Server-returned status text or message; never contains request bodies or auth data. */
  readonly message: string;
  /** The operation or service name that initiated the request. */
  readonly operation: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  private logger = inject(LoggerService);

  /**
   * Returns an RxJS error handler that resolves to an {@link ApplicationContract}.
   * Logs structured error context (AOS-1522) before returning a failure value.
   *
   * @param operation   Name of the operation or service making the request.
   * @param alternativeError  Optional override message for the failure response body.
   */
  public handle(operation = 'operation', alternativeError?: string) {
    return (error: HttpErrorResponse): Observable<ApplicationContract<any>> => {
      const response = new FailureResponse(alternativeError || '', error.status);
      this.logError(operation, error);
      if (error.status === 401) {
        // TODO: Add logout and redirect logic
        // this.authService.clearEverything();
        // this.router.navigate(['/auth', 'login']);
      }
      return of(response);
    };
  }

  /**
   * Returns an RxJS error handler that resolves to an {@link ApiContract}.
   * Logs structured error context (AOS-1522) before returning a failure value.
   *
   * @param operation   Name of the operation or service making the request.
   * @param alternativeError  Optional override message for the failure response body.
   */
  public handleError(operation = 'operation', alternativeError?: string) {
    return (error: HttpErrorResponse): Observable<ApiContract<any>> => {
      const response = new FailureContract(alternativeError || '', error.status);
      this.logError(operation, error);
      if (error.status === 401) {
        // TODO: Add logout and redirect logic
      }
      return of(response);
    };
  }

  /**
   * Logs a structured error context object for the given HTTP error.
   *
   * The logged object includes all fields required by AOS-1522:
   * status code, error class (4xx/5xx), endpoint, timestamp, and message.
   * No request bodies, auth tokens or user identifiers are ever logged.
   *
   * @param operation  Name of the calling operation or service.
   * @param error      The HTTP error response or generic error to log.
   */
  public logError(operation: string, error: HttpErrorResponse | Error | any): void {
    const context = this.buildErrorContext(operation, error);
    this.logger.error(operation, this.formatErrorMessage(error), error);
    this.logger.info(`${operation} failed`, context);
  }

  /**
   * Returns true if the status code is in the 4xx client-error range.
   */
  public isClientError(error: { status?: number } | any): boolean {
    const status = error?.status ?? 0;
    return status >= 400 && status < 500;
  }

  /**
   * Returns true if the status code is in the 5xx server-error range.
   */
  public isServerError(error: { status?: number } | any): boolean {
    const status = error?.status ?? 0;
    return status >= 500 && status < 600;
  }

  /**
   * Returns true if the status code is 0, indicating a network-level failure
   * (e.g. CORS block, DNS failure, or offline).
   */
  public isNetworkError(error: { status?: number } | any): boolean {
    return (error?.status ?? -1) === 0;
  }

  /**
   * Formats a human-readable error message string from any error value.
   * Safe to log: never exposes request bodies, tokens or stack traces.
   *
   * @param error Any error value; gracefully handles null/undefined.
   * @returns A concise, safe error summary string.
   */
  public formatErrorMessage(error: any): string {
    if (error == null) {
      return 'Unknown error';
    }

    if (error instanceof HttpErrorResponse) {
      const statusText = error.statusText || 'Unknown';
      return `HTTP ${error.status} ${statusText}: ${error.message || 'No message'}`;
    }

    if (error instanceof Error) {
      return error.message || 'Unknown error';
    }

    if (typeof error === 'string') {
      return error;
    }

    if (typeof error?.message === 'string') {
      return error.message;
    }

    return 'Unknown error';
  }

  /**
   * Builds the structured {@link HttpErrorContext} for a given operation and error.
   * Used for consistent, parseable log entries per AOS-1522.
   */
  private buildErrorContext(
    operation: string,
    error: HttpErrorResponse | Error | any
  ): HttpErrorContext {
    if (error instanceof HttpErrorResponse) {
      // Strip query parameters from the URL to avoid accidentally logging
      // any user-identifying data that may appear as query params.
      const safeEndpoint = error.url
        ? error.url.split('?')[0]
        : 'unknown';

      return {
        statusCode: error.status,
        errorClass: this.classifyStatus(error.status),
        endpoint: safeEndpoint,
        method: 'HTTP',
        timestamp: new Date().toISOString(),
        message: error.statusText || 'No status text',
        operation,
      };
    }

    return {
      statusCode: 0,
      errorClass: 'Network Error',
      endpoint: 'unknown',
      method: 'unknown',
      timestamp: new Date().toISOString(),
      message: error instanceof Error ? error.message : 'Unknown error',
      operation,
    };
  }

  /**
   * Maps an HTTP status code to the AOS-1522 error classification label.
   */
  private classifyStatus(
    status: number
  ): HttpErrorContext['errorClass'] {
    if (status >= 500) return '5xx - Server-Side Error';
    if (status >= 400) return '4xx - Client-Side Error';
    return 'Network Error';
  }
}
